import type { CanvasStarterTemplate } from '@kennisbank/shared';

/**
 * Canvas starter-template picker (issue #3).
 *
 * Surfaced when the user clicks the "Canvas" button next to the chat
 * input AND no canvas yet exists on the active chat. The picker lets the
 * user pre-seed structure (feature spec, client presentation, test cases,
 * marketing one-pager) or start blank. On selection, the parent component
 * stages the chosen starter on the next /ask call (canvasMode='create').
 */

interface StarterChoice {
  value: CanvasStarterTemplate;
  label: string;
  description: string;
  defaultExport: 'docx' | 'pptx';
}

const CHOICES: StarterChoice[] = [
  {
    value: 'blank',
    label: 'Blank',
    description: 'Free-form. The agent follows your instructions exactly.',
    defaultExport: 'docx',
  },
  {
    value: 'spec',
    label: 'Feature spec',
    description: 'Context, current state, proposed change, acceptance criteria, rollback, effort.',
    defaultExport: 'docx',
  },
  {
    value: 'client_presentation',
    label: 'Client presentation',
    description: 'Customer-facing deck. One slide per section in the pptx export.',
    defaultExport: 'pptx',
  },
  {
    value: 'test_cases',
    label: 'Test cases',
    description: 'Scope, pre-conditions, test cases table, edge cases.',
    defaultExport: 'docx',
  },
  {
    value: 'marketing_onepager',
    label: 'Marketing one-pager',
    description: 'Single-page customer summary. ≤700 words, outcomes-led.',
    defaultExport: 'docx',
  },
];

export interface CanvasStarterPickerProps {
  onChoose: (template: CanvasStarterTemplate) => void;
  onCancel: () => void;
}

export function CanvasStarterPicker({ onChoose, onCancel }: CanvasStarterPickerProps) {
  return (
    <div
      role="dialog"
      aria-label="Start a canvas"
      className="absolute bottom-full left-0 mb-2 w-[420px] rounded-lg border border-nebula-gray-5 bg-white shadow-lg"
    >
      <div className="border-b border-nebula-gray-5 px-4 py-3">
        <p className="font-display text-sm font-semibold text-nebula-primary">Start a canvas</p>
        <p className="mt-0.5 text-xs text-nebula-fg-1">
          Pick a starter template or start blank. You can export as docx or pptx later.
        </p>
      </div>
      <ul className="max-h-[380px] overflow-y-auto py-1">
        {CHOICES.map((c) => (
          <li key={c.value}>
            <button
              type="button"
              onClick={() => onChoose(c.value)}
              className="w-full text-left px-4 py-3 hover:bg-nebula-tint focus:bg-nebula-tint focus:outline-none"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-nebula-fg-1">{c.label}</span>
                <span className="text-[10px] uppercase tracking-wide text-nebula-secondary">
                  default: {c.defaultExport}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-nebula-fg-1/70">{c.description}</p>
            </button>
          </li>
        ))}
      </ul>
      <div className="flex justify-end border-t border-nebula-gray-5 px-4 py-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-nebula-fg-1/60 hover:text-nebula-fg-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
